import { cloneDeep, isEmpty, isNil } from 'lodash';
import React, { ReactNode, useCallback, useContext } from 'react';
import { View as RNView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { SubformContent } from '@shared/components/blocks/lib/inputSubforms/SubformsContent';
import { SubformsSheet } from '@shared/components/blocks/lib/inputSubforms/sheet/SubformsSheet';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextSubtitle } from '@shared/components/typography';
import { Field } from '@shared/data/entity/fields/Field';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { useWorkflowTemplate, useWorkspaceDetails } from '@shared/scenes/workflows/hooks';
import { getComponentIcon, isAuditTagComponent } from '@shared/scenes/workflows/utils';
import { WorkflowView } from '@shared/scenes/workflows/views/view-components/WorkflowView';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { MBAlert } from '@shared/util/alert';
import { BUILDER_SUBFORM_LINK, useFeatureFlag } from '@shared/util/featureFlags/launchDarkly';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import {
  MBNavStackProvider,
  SubformRoute,
  useBuilderLink,
  useNavStackContext,
} from '@shared/util/navigation';
import { FieldRecord } from '../../../types';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { EditFieldContainer } from '../EditFieldContainer';
import { WorkflowCreate } from './WorkflowCreate';

interface Props {
  field: SubformField | null;
  component: MBAPI.WorkflowTemplate_components_SubformComponent;
  workflowTemplateId: string;
  updateField: (componentId: string, field: SubformField | null) => void;
  error?: string;
  allowCreate?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function SubformEditField({
  field,
  component,
  workflowTemplateId,
  updateField,
  error,
  allowCreate = true,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const [isSheetOpen, openSheet, closeSheet] = useBoolean(false);
  const workspaceDetails = useWorkspaceDetails();
  const hasSubform = !isNil(field?.subform);
  const nav = useNavStackContext<SubformRoute>();
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const isBuilderSubformLinkEnabled = useFeatureFlag(BUILDER_SUBFORM_LINK);
  const isReadOnly = component.readonly ?? false;
  const navigateToBuilder = useBuilderLink(workflowTemplateId);
  const anchorName = `--subform-edit-field-${component.id}`;

  const onSelectSubform = useCallback(
    (subformWorkflowTemplate: MBAPI.WorkflowTemplate) => {
      const componentsWithDefaults =
        subformWorkflowTemplate.components
          ?.filter(isAuditTagComponent)
          .filter(c => !isEmpty(c.default?.value)) ?? [];

      const defaultData = componentsWithDefaults.reduce((acc, subformComponent) => {
        acc[subformComponent.id] = { tags: subformComponent.default?.value };
        return acc;
      }, {});

      updateField(
        component.id,
        new SubformField({ workflowTemplateId: subformWorkflowTemplate.id, data: defaultData }),
      );
      closeSheet();
    },
    [component.id, updateField, closeSheet],
  );

  const navigateToAdd = useCallback(() => {
    if (isBuilderSubformLinkEnabled) {
      navigateToBuilder({ route: '/subform', isNested: true, includeMenu: false });
    } else {
      nav.push({
        name: 'WorkflowCreate',
        params: {
          channelSlug: workspaceDetails.get(workflowTemplateId)?.baseNavigationPath ?? '',
          originWorkflowTemplateId: workflowTemplate?.id,
          originSubformComponentId: component.id,
          workspaceDetails: workspaceDetails,
        },
      });
      closeSheet();
    }
  }, [
    closeSheet,
    component.id,
    nav,
    workflowTemplate?.id,
    workflowTemplateId,
    workspaceDetails,
    isBuilderSubformLinkEnabled,
    navigateToBuilder,
  ]);

  const onSelect = useCallback(
    (subformId: string) => {
      nav.push({
        name: 'subform',
        params: {
          workflowTemplateId: subformId,
          parentComponentId: component.id,
          isReadOnly,
        },
      });
      closeSheet();
    },
    [component.id, nav, closeSheet, isReadOnly],
  );

  const onClear = useCallback(() => {
    MBAlert.confirm({
      message: i18n('card.blocks.subformsInput.confirmClear'),
      confirmText: i18n('common.ok'),
      onConfirm: () => {
        updateField(component.id, null);
      },
    });
  }, [component.id, updateField]);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showLabel={true}
      indentLabel={true}
      showIcon={false}
      largerInput={false}
      error={error}
      style={style}
    >
      <MBTouchableOpacity
        onPress={hasSubform || isReadOnly ? undefined : openSheet}
        style={{
          marginHorizontal: responsiveForms ? undefined : -grid(1),
          paddingVertical: responsiveForms ? undefined : grid(0.5),
        }}
      >
        {hasSubform && (
          <SubformContainer
            {...{
              field,
              onSelect,
              onClear,
              isReadOnly,
            }}
          />
        )}
        {!hasSubform && <EmptyContainer {...{ component }} />}
        {isSheetOpen && (
          <SubformsSheet
            anchorName={anchorName}
            blockLabel={component.label ?? ''}
            workflowTemplate={workflowTemplate}
            onClose={closeSheet}
            onSelect={onSelectSubform}
            navigateToAdd={allowCreate ? navigateToAdd : undefined}
          />
        )}
      </MBTouchableOpacity>
    </EditFieldContainer>
  );
}

function SubformContainer({
  field,
  onSelect,
  onClear,
  isReadOnly = false,
}: {
  field: SubformField | null;
  onSelect: (subformId: string) => void;
  onClear: VoidFunction;
  isReadOnly?: boolean;
}) {
  const responsiveForms = useResponsiveFormContext();
  if (isNil(field?.subform)) {
    return null;
  }
  return (
    <RNView style={[styles.subformContainer, responsiveForms && styles.responsiveSubformContainer]}>
      <SubformContent
        subform={field.subform}
        onSelect={onSelect}
        onClear={onClear}
        isReadOnly={isReadOnly}
      />
    </RNView>
  );
}

function EmptyContainer({
  component,
}: {
  component: MBAPI.WorkflowTemplate_components_SubformComponent;
}) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const containerColors = {
    backgroundColor: colors.backgroundInactive,
    borderColor: colors.input.borderInactive,
  };
  const responsiveFormStyle = {
    backgroundColor: Colors.v2.transparent,
    borderColor: Colors.v2.transparent,
  };

  return (
    <View paddingHorizontal={responsiveForms ? 0 : 1}>
      <MBView
        style={[styles.container, containerColors, responsiveForms && responsiveFormStyle]}
        row
        centerVertically
        centerHorizontally
        paddingLeft={1}
        paddingRight={2}
      >
        <View marginRight={1}>
          <MBIconV2
            color={colors.foregroundActive}
            size={grid(5)}
            name={getComponentIcon(component) ?? 'list'}
          />
        </View>
        <MBTextSubtitle
          numberOfLines={1}
          ellipsizeMode="tail"
          color={colors.foregroundActive}
          style={{ flexGrow: 0, flexShrink: 1 }}
        >
          {component.placeholder}
        </MBTextSubtitle>
      </MBView>
    </View>
  );
}

interface SubformNavigationContextProps {
  fields: FieldRecord | null;
  updateField: (componentId: string, field: Field | null) => void;
  children: ReactNode;
}

export function SubformNavigationContext(props: SubformNavigationContextProps) {
  return (
    <MBNavStackProvider<SubformRoute> initialRoutes={[{ name: 'cardForm' }]}>
      <ConditionalNav {...props} />
    </MBNavStackProvider>
  );
}

function ConditionalNav({ fields, updateField, children }: SubformNavigationContextProps) {
  const navContext = useNavStackContext<SubformRoute>();

  if (navContext.currentRoute?.name === 'subform' && !isNil(fields)) {
    const workflowTemplateId = navContext.currentRoute.params?.workflowTemplateId ?? '';
    const parentComponentId = navContext.currentRoute.params?.parentComponentId ?? '';
    const isReadOnly = navContext.currentRoute.params?.isReadOnly ?? false;

    const field: SubformField = fields[parentComponentId] as SubformField;
    const initialData = isNil(field?.subform) ? {} : cloneDeep(field.subform.data);

    const onSubmit = (data: Record<string, SupportedSubformComponentValue>) => {
      updateField(parentComponentId, new SubformField({ workflowTemplateId, data }));
      navContext.pop();
    };

    return (
      <WorkflowView
        workflowTemplateId={workflowTemplateId}
        initialData={initialData}
        onSubmit={onSubmit}
        isReadOnly={isReadOnly}
      />
    );
  }

  if (navContext.currentRoute?.name === 'WorkflowCreate') {
    const params = navContext.currentRoute.params;
    return (
      <WorkflowCreate
        channelSlug={params?.channelSlug ?? ''}
        originWorkflowTemplateId={params?.originWorkflowTemplateId ?? ''}
        workspaceDetails={params?.workspaceDetails ?? new Map()}
        originSubformComponentId={params?.originSubformComponentId ?? ''}
      />
    );
  }

  return <>{children}</>;
}

const styles = StyleSheet.create({
  container: {
    borderRadius: grid(1),
    borderStyle: 'dashed',
    borderWidth: 2,
    paddingVertical: grid(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  subformContainer: {
    paddingHorizontal: grid(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  responsiveSubformContainer: {
    paddingHorizontal: 0,
  },
});
